/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import copyToClipboard from 'copy-to-clipboard';
import { useLocation } from 'react-router-dom';

import { AlertType, ShowAlert } from 'ha/modules/Alertbar/types';
import { Popover, PopoverProps } from '@hbf/dsl';
import { useConfig } from 'ha/helpers/config';
import { useIntl } from 'ha/i18n';
import { DialogNext as Dialog } from 'ha/components/Dialog';
import { ShareTargets, getShareUrls } from 'ha/utils/socialShareURLs';

import { SocialShareMenu } from './SocialShareMenu';

const POPOVER_ORIGIN: PopoverProps['anchorOrigin'] = {
  horizontal: 'right',
  vertical: 'top',
};

export interface Props {
  children: ({
    shareTriggerRef,
    handleShareClick,
  }: {
    shareTriggerRef: React.RefObject<HTMLButtonElement>;
    handleShareClick: () => void;
  }) => JSX.Element | null;
  isMobile?: boolean;
  message?: string;
  path?: string;
  shareTargets?: ShareTargets[];
  trackingEventName?: string;
  trackingPayload?: Record<string, unknown>;
}

interface ExtraProps {
  mobilePreferNativeShare?: boolean;
  showAlert: (options: ShowAlert) => void;
  track: (eventName: string, params: {}) => void;
}

const useStyles = makeStyles()({
  dialogContent: {
    minWidth: '200px',
  },
});

const SocialShare: React.FC<Props & ExtraProps> = ({
  children,
  isMobile = false,
  message,
  mobilePreferNativeShare = false,
  path,
  shareTargets = [
    ShareTargets.copy,
    ShareTargets.facebookSend,
    ShareTargets.facebook,
    ShareTargets.messenger,
    ShareTargets.whatsapp,
  ],
  showAlert,
  track,
  trackingEventName = 'Share target clicked',
  trackingPayload,
}) => {
  const { baseDomain } = useConfig();
  const { T } = useIntl();
  const { classes } = useStyles();
  const location = useLocation();

  const [open, setOpen] = React.useState(false);

  const [showPopup, setShowPopup] = React.useState(false);

  const shareTriggerRef = React.useRef<HTMLButtonElement | null>(null);

  const handleShareClick = React.useCallback(() => setOpen(true), []);

  const handleShareClose = React.useCallback(() => setOpen(false), []);

  const shareUrl = `${baseDomain}${path || location.pathname}`;

  React.useEffect(() => {
    // use popup when feature is disabled ot native share is not supported
    if (!mobilePreferNativeShare || !global.navigator?.share) {
      setShowPopup(open);

      return;
    }

    if (open) {
      const shareMessage = message;

      // try to show native UI
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      navigator.share!({
        url: shareUrl,
        title: shareMessage,
      })
        .then(() => {
          track(trackingEventName, { ...trackingPayload, target: 'native' });

          // fixes navigator.share() promise not fulfilling when popup is closed on Android.
          setTimeout(handleShareClose);
        })
        .catch(error => {
          if (error.name === 'AbortError' && error.message.includes('cancel')) {
            // Chrome on Android: "Share canceled"
            // Safari on iOS and desktop: "Abort due to cancellation of share."
            // mark native as closed
            setTimeout(handleShareClose);
          } else {
            // fallback to popup
            setShowPopup(true);
          }
        });
    } else {
      setShowPopup(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleShareTargetClick = React.useCallback((target: ShareTargets) => {
    if (target === ShareTargets.copy) {
      copyToClipboard(shareUrl, {
        format: 'text/plain',
      });

      showAlert({
        dismissAfter: 4000,
        kind: AlertType.Info,
        text: T('Link copied'),
      });
    } else {
      const urls = getShareUrls(shareUrl, message);

      window.open(urls[target]);
    }

    track(trackingEventName, { ...trackingPayload, target });

    handleShareClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      {children({ shareTriggerRef, handleShareClick })}

      {isMobile ? (
        <Dialog
          content={
            <SocialShareMenu
              isMobile
              onShareTargetClicked={handleShareTargetClick}
              shareTargets={shareTargets}
            />
          }
          data-test-locator="SocialShare"
          fullScreen={false}
          keepMounted
          noContentPadding
          onClose={handleShareClose}
          PaperProps={{ className: classes.dialogContent }}
          show={showPopup}
          title={T('Share')}
        />
      ) : (
        <Popover
          anchorEl={shareTriggerRef.current}
          anchorOrigin={POPOVER_ORIGIN}
          data-test-locator="SocialShare"
          keepMounted
          onClose={handleShareClose}
          open={open}
          transformOrigin={POPOVER_ORIGIN}
        >
          <SocialShareMenu
            onShareTargetClicked={handleShareTargetClick}
            shareTargets={shareTargets}
          />
        </Popover>
      )}
    </React.Fragment>
  );
};

export { SocialShare };
